import React, { useState, useContext, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./styles.scss";
import AuthLayout from "@/layouts/auth-layout";
import Icon from "@/components/Icon";
import { Checkbox } from "@/components/CheckBox/CheckBox";
import { useAuth } from "@/contexts/auth-context";
import { FaEyeSlash, FaEye } from "react-icons/fa";
import { toast } from "sonner";
import z from "zod";

const passwordSchema = z.object({
  password: z
    .string()
    .min(8)
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(
      /[^a-zA-Z0-9]/,
      "Password must contain at least one special character"
    )
    .regex(/[a-z]/, "Password must contain at least one letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(
      /[^a-zA-Z0-9]/,
      "Password must contain at least one special character"
    ),
});

// Interfaces
interface FormData {
  firstName: string;
  lastName: string;
  age: Date | null;
  email: string;
  password: string;
  agreeToTerms: boolean;
  parentFirstName?: string;
  parentLastName?: string;
  parentAge?: Date | null;
  parentEmail?: string;
  parentAgreeToTerms?: boolean;
}

interface AuthContextType {
  register: (payload: any, navigate: (path: string) => void) => Promise<void>;
  registerLoading: boolean;
  setRegisterLoading: (loading: boolean) => void;
  modalNetworkVisible: boolean;
  setModalNetworkVisible: (visible: boolean) => void;
}

// Mock AuthContext
const AuthContext = React.createContext<AuthContextType>({
  register: async () => {},
  registerLoading: false,
  setRegisterLoading: () => {},
  modalNetworkVisible: false,
  setModalNetworkVisible: () => {},
});

interface ProgressBarProps {
  level: number;
  step: number;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  previousStep: () => void;
}

const AppProgressBar: React.FC<ProgressBarProps> = ({
  level,
  step,
  currentStep,
  setCurrentStep,
  previousStep,
}) => (
  <>
    <button className="back-button" onClick={previousStep}>
      <Icon name="left_arrow" />
      Back
    </button>
    <div className="progress-bar">
      <div className="progress-bar-inner" style={{ width: `${level}%` }}></div>
    </div>
  </>
);

interface ButtonProps {
  title: string;
  onPress: () => void;
  isSubmitting?: boolean;
  variant?: string;
  style?: React.CSSProperties;
}

export const Button: React.FC<ButtonProps> = ({
  title,
  onPress,
  isSubmitting,
  variant,
  style,
}) => (
  <button
    className={`app-button ${variant || "primary"}`}
    onClick={onPress}
    disabled={isSubmitting}
    style={style}
  >
    {title}
  </button>
);

interface StepProps {
  formData: FormData;
  updateFormData: (data: Partial<FormData>) => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  previousStep: () => void;
  errors: Partial<Record<keyof FormData, string>>;
}

const Step1: React.FC<StepProps & { goBack: () => void }> = ({
  formData,
  updateFormData,
  currentStep,
  setCurrentStep,
  previousStep,
  goBack,
  errors,
}) => (
  <div className="step-wrapper">
    <AppProgressBar
      level={20}
      step={1}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      previousStep={previousStep}
    />
    <div className="step-header">
      <h2 className="header-text">What is your name?</h2>
    </div>
    <div className="form-inputs">
      <div className="input-container">
        <label className="input-label">First Name</label>
        <input
          className="text-input"
          value={formData.firstName}
          onChange={(e) => updateFormData({ firstName: e.target.value })}
          autoCapitalize="words"
        />
        {errors.firstName && (
          <span className="error-text">{errors.firstName}</span>
        )}
      </div>
      <div className="input-container">
        <label className="input-label">Last Name</label>
        <input
          className="text-input"
          value={formData.lastName}
          onChange={(e) => updateFormData({ lastName: e.target.value })}
          autoCapitalize="words"
        />
        {errors.lastName && (
          <span className="error-text">{errors.lastName}</span>
        )}
      </div>
    </div>
  </div>
);

const Step2: React.FC<StepProps> = ({
  formData,
  updateFormData,
  currentStep,
  setCurrentStep,
  previousStep,
  errors,
}) => {
  const maxDate = new Date(new Date().getTime() - 94608000000);
  const minDate = new Date(1900, 0, 1);

  const dateValue =
    formData.age instanceof Date && !isNaN(formData.age.getTime())
      ? formData.age.toISOString().split("T")[0]
      : "";

  return (
    <div className="step-wrapper">
      <AppProgressBar
        level={40}
        step={2}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        previousStep={previousStep}
      />
      <div className="step-header">
        <h2 className="header-text">Date Of Birth</h2>
      </div>
      <div className="form-inputs">
        <div className="input-container">
          <label className="input-label">Enter Your Date Of Birth</label>
          <input
            contentEditable={false}
            type="date"
            className="date-input"
            max={maxDate.toISOString().split("T")[0]}
            min={minDate.toISOString().split("T")[0]}
            value={dateValue}
            onChange={(e) => {
              updateFormData({ age: new Date(e.target.value) });
            }}
          />
          {errors.age && <span className="error-text">{errors.age}</span>}
        </div>
      </div>
    </div>
  );
};

const Step3: React.FC<
  StepProps & { showForm: boolean; setShowForm: (show: boolean) => void }
> = ({
  formData,
  updateFormData,
  currentStep,
  setCurrentStep,
  previousStep,
  showForm,
  setShowForm,
  errors,
}) => {
  const navigate = useNavigate();
  const maxDate = new Date(new Date().getTime() - 94608000000);
  const minDate = new Date(1900, 0, 1);

  const dateValue =
    formData.parentAge instanceof Date && !isNaN(formData.parentAge.getTime())
      ? formData.parentAge.toISOString().split("T")[0]
      : "";

  return (
    <div className="step-wrapper">
      <AppProgressBar
        level={60}
        step={3}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        previousStep={previousStep}
      />
      <div className="step-header">
        <h2 className="header-text">Guardian Consent</h2>
      </div>
      {showForm ? (
        <div className="form-inputs">
          <div className="input-container">
            <label className="input-label">First Name</label>
            <input
              className="text-input"
              value={formData.parentFirstName || ""}
              onChange={(e) =>
                updateFormData({ parentFirstName: e.target.value })
              }
            />
            {errors.parentFirstName && (
              <span className="error-text">{errors.parentFirstName}</span>
            )}
          </div>
          <div className="input-container">
            <label className="input-label">Last Name</label>
            <input
              className="text-input"
              value={formData.parentLastName || ""}
              onChange={(e) =>
                updateFormData({ parentLastName: e.target.value })
              }
            />
            {errors.parentLastName && (
              <span className="error-text">{errors.parentLastName}</span>
            )}
          </div>
          <div className="input-container">
            <label className="input-label">Date Of Birth</label>
            <input
              type="date"
              className="date-input"
              max={maxDate.toISOString().split("T")[0]}
              min={minDate.toISOString().split("T")[0]}
              value={dateValue}
              onChange={(e) =>
                updateFormData({ parentAge: new Date(e.target.value) })
              }
            />
            {errors.parentAge && (
              <span className="error-text">{errors.parentAge}</span>
            )}
          </div>
          <div className="input-container">
            <label className="input-label">Email</label>
            <input
              className="text-input"
              type="email"
              value={formData.parentEmail || ""}
              onChange={(e) => updateFormData({ parentEmail: e.target.value })}
            />
            {errors.parentEmail && (
              <span className="error-text">{errors.parentEmail}</span>
            )}
          </div>
          <div className="input-container checkbox-wrapper">
            <Checkbox
              label="Accept Terms and Condition"
              isTerms={true}
              checked={formData.parentAgreeToTerms || false}
              onChange={(checked) =>
                updateFormData({ parentAgreeToTerms: checked })
              }
            />
            {errors.parentAgreeToTerms && (
              <span className="error-text">{errors.parentAgreeToTerms}</span>
            )}
          </div>
        </div>
      ) : (
        <div className="consent-prompt">
          <p>To proceed, we require consent from a parent or legal guardian.</p>
          <div className="consent-buttons">
            <Button title="Continue" onPress={() => setShowForm(true)} />
            <Button
              title="Decline"
              onPress={() => navigate("/")}
              variant="tertiary"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const Step4: React.FC<StepProps> = ({
  formData,
  updateFormData,
  currentStep,
  setCurrentStep,
  previousStep,
  errors,
}) => (
  <div className="step-wrapper">
    <AppProgressBar
      level={80}
      step={4}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      previousStep={previousStep}
    />
    <div className="step-header">
      <h2 className="header-text">What's your email address?</h2>
    </div>
    <div className="form-inputs">
      <div className="input-container">
        <label className="input-label">Email</label>
        <input
          className="text-input"
          type="email"
          value={formData.email}
          onChange={(e) => updateFormData({ email: e.target.value })}
        />
        {errors.email && <span className="error-text">{errors.email}</span>}
      </div>
    </div>
  </div>
);

const Step5: React.FC<StepProps> = ({
  formData,
  updateFormData,
  currentStep,
  setCurrentStep,
  previousStep,
  errors,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [check, setCheck] = useState({
    minLength: false,
    hasLetter: false,
    hasNumber: false,
    hasSpecial: false,
  });
  const [feedBack, setFeedback] = useState<string[]>([]);

  const result = passwordSchema.safeParse({
    password: formData.password,
  });
  function getPasswordStatus(password: string) {
    const feedback: string[] = [];

    if (!result.success) {
      const passwordErrors = result.error.issues.filter(
        (issue) => issue.path[0] === "password"
      );

      passwordErrors.forEach((issue) => {
        feedback.push(issue.message);
      });
    }

    const checks = {
      minLength: formData.password.length >= 8,
      hasLetter: /[a-zA-Z]/.test(formData.password),
      hasNumber: /[0-9]/.test(formData.password),
      hasSpecial: /[^a-zA-Z0-9]/.test(formData.password),
    };
    setCheck({ ...check, ...checks });
    setFeedback(feedback);

    return {
      isValid: result.success,
      checks,
    };
  }

  useEffect(() => {
    getPasswordStatus(formData.password);
  }, [formData.password]);
  const values = Object.values(check);

  return (
    <div className="step-wrapper">
      <AppProgressBar
        level={100}
        step={5}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        previousStep={previousStep}
      />
      <div className="step-header">
        <h2 className="header-text">Create your password</h2>
      </div>
      <div className="form-inputs">
        <div className="input-container password-container">
          <label className="input-label">Password</label>
          <div className="setPbox">
            <input
              type={showPassword ? "text" : "password"}
              className="text-input"
              value={formData.password}
              onChange={(e) => updateFormData({ password: e.target.value })}
            />
            <div className="password-toggle">
              {showPassword ? (
                <FaEyeSlash
                  color="#169FED"
                  size={20}
                  onClick={() => setShowPassword(false)}
                  className="icon"
                />
              ) : (
                <FaEye
                  color="#169FED"
                  size={20}
                  onClick={() => setShowPassword(true)}
                  className="icon"
                />
              )}
            </div>
          </div>
          <div className="theBox">
            <div
              className={`
               ${
                 values?.filter((v) => v === true).length === 1
                   ? "red"
                   : values.filter((v) => v === true).length === 2 ||
                     values.filter((v) => v === true).length === 3
                   ? "orange"
                   : values.filter((v) => v === true).length === 4 && "green"
                 //  : password.length && values?.filter((v) => v === true).length < 1 && "lines"
               }
               lines`}
            ></div>
            <div
              className={`${
                values.filter((v) => v === true).length === 2 ||
                values.filter((v) => v === true).length === 3
                  ? "orange"
                  : values.filter((v) => v === true).length === 4 && "green"
              } lines`}
            ></div>
            <div
              className={`${
                values.filter((v) => v === true).length === 4 && "green"
              } lines`}
            ></div>
          </div>
        </div>
        {errors.password && (
          <span className="error-text">{errors.password}</span>
        )}
        <div className="input-container checkbox-wrapper">
          <Checkbox
            label="Accept Terms and Condition"
            isTerms={true}
            checked={formData.agreeToTerms}
            onChange={(checked) => updateFormData({ agreeToTerms: checked })}
          />
          {errors.agreeToTerms && (
            <span className="error-text">{errors.agreeToTerms}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Register Component
const Register: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    age: null,
    email: "",
    password: "",
    agreeToTerms: false,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>(
    {}
  );

  const navigate = useNavigate();
  const { register, registerLoading, setRegisterLoading } = useAuth();

  const calculateAge = useCallback((dob: Date | null): number => {
    if (!dob) return 0;
    const birthDate = new Date(dob);
    const difference = Date.now() - birthDate.getTime();
    const ageDate = new Date(difference);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  }, []);

  const validateStep = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (currentStep === 1) {
      if (!formData.firstName.trim())
        newErrors.firstName = "First name is required";
      if (!formData.lastName.trim())
        newErrors.lastName = "Last name is required";
    } else if (currentStep === 2) {
      if (!formData.age) newErrors.age = "Date of birth is required";
    } else if (currentStep === 3 && showForm) {
      if (!formData.parentFirstName?.trim())
        newErrors.parentFirstName = "Parent first name is required";
      if (!formData.parentLastName?.trim())
        newErrors.parentLastName = "Parent last name is required";
      if (!formData.parentAge)
        newErrors.parentAge = "Parent date of birth is required";
      if (!formData.parentEmail?.trim())
        newErrors.parentEmail = "Parent email is required";
      if (!formData.parentAgreeToTerms)
        newErrors.parentAgreeToTerms = "You must accept the terms";
    } else if (currentStep === 4) {
      if (!formData.email.trim()) newErrors.email = "Email is required";
      else if (!/\S+@\S+\.\S+/.test(formData.email))
        newErrors.email = "Invalid email format";
    } else if (currentStep === 5) {
      if (!formData.password.trim())
        newErrors.password = "Password is required";
      else if (formData.password.length < 6)
        newErrors.password = "Password must be at least 6 characters";
      if (!formData.agreeToTerms)
        newErrors.agreeToTerms = "You must accept the terms";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [currentStep, formData, showForm]);

  const updateFormData = useCallback((data: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  }, []);

  const nextStep = useCallback(() => {
    if (!validateStep()) return;

    const age = calculateAge(formData.age);
    if (currentStep === 2) {
      setCurrentStep(age < 13 ? 3 : 4);
    } else {
      setCurrentStep((curr) => curr + 1);
    }
  }, [currentStep, formData.age, calculateAge, validateStep]);

  const previousStep = useCallback(() => {
    if (currentStep === 1) {
      navigate("/");
    } else if (currentStep === 4) {
      const dob = formData.age;
      const age = calculateAge(dob);
      if (age < 13) {
        setShowForm(false);
        setCurrentStep(3);
      } else {
        setCurrentStep(2);
      }
    } else if (currentStep === 3 && showForm) {
      setShowForm(false);
    } else {
      setCurrentStep((curr) => curr - 1);
    }
  }, [currentStep, showForm, calculateAge, navigate, formData]);

  const handleSubmit = useCallback(async () => {
    if (!validateStep()) return;

    if (currentStep < 5) {
      nextStep();
      return;
    }

    const age = calculateAge(formData.age);
    let payload: any = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      date_of_birth: formData.age,
      email: formData.email,
      password: formData.password,
    };

    if (age < 13) {
      payload.guardian = {
        firstName: formData.parentFirstName,
        lastName: formData.parentLastName,
        dateOfBirth: formData.parentAge,
        Email: formData.parentEmail,
      };
    }

    const result = passwordSchema.safeParse({
      password: formData.password,
    });

    if (result.success) {
      try {
        setRegisterLoading(true);
        await register(payload, navigate);
      } catch (error: any) {
        const message = error?.response?.data?.message || "An error occurred";
        toast.error(message, {
          richColors: true,
        });
        if (
          message === "User already exists" ||
          message.includes("duplicate key value")
        ) {
          setErrorMessage(message);
        } else {
          console.error("Registration error:", message);
        }
      } finally {
        setRegisterLoading(false);
      }
    } else {
      toast.error("Make sure your password is strong enough");
    }
  }, [
    currentStep,
    formData,
    calculateAge,
    validateStep,
    nextStep,
    register,
    navigate,
    setRegisterLoading,
  ]);

  const renderStep = useCallback(() => {
    const steps: Record<number, JSX.Element> = {
      1: (
        <Step1
          formData={formData}
          updateFormData={updateFormData}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          previousStep={previousStep}
          goBack={() => navigate("/")}
          errors={errors}
        />
      ),
      2: (
        <Step2
          formData={formData}
          updateFormData={updateFormData}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          previousStep={previousStep}
          errors={errors}
        />
      ),
      3: (
        <Step3
          formData={formData}
          updateFormData={updateFormData}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          previousStep={previousStep}
          showForm={showForm}
          setShowForm={setShowForm}
          errors={errors}
        />
      ),
      4: (
        <Step4
          formData={formData}
          updateFormData={updateFormData}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          previousStep={previousStep}
          errors={errors}
        />
      ),
      5: (
        <Step5
          formData={formData}
          updateFormData={updateFormData}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          previousStep={previousStep}
          errors={errors}
        />
      ),
    };
    return steps[currentStep];
  }, [
    currentStep,
    formData,
    updateFormData,
    previousStep,
    showForm,
    errors,
    navigate,
  ]);

  const age = calculateAge(formData.age);

  return (
    <AuthLayout>
      <div className="register-container">
        {registerLoading && <div className="app-loader">Loading...</div>}
        {errorMessage.toLowerCase() === "user already exists" && (
          <div className="error-modal">
            <div className="error-modal-content">
              <p className="error-text">{errorMessage}</p>
              <Button
                title="Try a different email"
                onPress={() => {
                  setCurrentStep(4);
                  setErrorMessage("");
                }}
              />
              <Button title="Go to Login" onPress={() => navigate("/login")} />
            </div>
          </div>
        )}
        <div
          className="form-container"
          style={{
            marginBottom: age < 13 && showForm ? "20%" : "80%",
          }}
        >
          {renderStep()}
          <div className="button-container">
            {currentStep === 3 && age < 13 && !showForm ? null : (
              <Button
                style={{ width: "100%" }}
                title={
                  registerLoading
                    ? "Loading..."
                    : currentStep === 5
                    ? "Register"
                    : "Continue"
                }
                onPress={handleSubmit}
                isSubmitting={registerLoading}
              />
            )}
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Register;
